/**
 * @fileoverview The Composer component is the main interface for creating and scheduling social media posts.
 * It includes a text editor, platform selector, media uploader, scheduling options, and AI-powered
 * content generation and refinement features.
 */
import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { type Post, Platform, type Media, type MediaType, type PostVisibility, type ComposerDraft, type FacebookPageConnection, type InstagramAccountConnection } from '../types';
import { PlatformSelector } from './PlatformSelector';
import { MediaUploader } from './ImageUploader';
import { GenerateSuggestionModal } from './GenerateSuggestionModal';
import { MagicWandIcon, GlobeIcon, UsersIcon, LockClosedIcon, CalendarIcon, InformationCircleIcon } from './icons';
import { PlatformPreview } from './PlatformPreview';
import { Tooltip } from './Tooltip';

interface ComposerProps {
  onSchedulePost: (post: Omit<Post, 'id' | 'status'>, mediaDescription: string | null) => Promise<void> | void;
  generatePostSuggestion: (prompt: string) => Promise<string>;
  refinePostSuggestion: (originalPost: string, instruction: string) => Promise<string>;
  postToReuse: Post | null;
  onClearReusedPost: () => void;
  mediaToReuse: Media | null;
  onClearReusedMedia: () => void;
  showSnackbar: (message: string) => void;
  facebookPages: FacebookPageConnection[];
  facebookPagesLoading: boolean;
  onRequestFacebookConnect: () => void;
  instagramAccounts: InstagramAccountConnection[];
  instagramAccountsLoading: boolean;
  onRequestInstagramConnect: () => void;
  canPublishWithoutApproval: boolean;
}

// Character limits for different social media platforms.
const platformLimits: Partial<Record<Platform, number>> = {
  [Platform.X]: 280,
  [Platform.Instagram]: 2200,
  [Platform.Facebook]: 5000,
  [Platform.YouTube]: 5000,
  [Platform.Mastodon]: 500,
  [Platform.Threads]: 500,
  [Platform.Bluesky]: 300,
  [Platform.TikTok]: 2200,
};

// Platforms that support post visibility settings.
const PLATFORMS_WITH_VISIBILITY: Platform[] = [Platform.Facebook, Platform.X, Platform.Mastodon];

// Styled display names for each platform.
const platformDisplay: Record<Platform, React.ReactNode> = {
    [Platform.Facebook]: <span className="font-bold text-[#1877F2]">{Platform.Facebook}</span>,
    [Platform.Instagram]: <span className="font-bold text-[#E1306C]">{Platform.Instagram}</span>,
    [Platform.X]: <span className="font-bold text-black dark:text-white">{Platform.X}</span>,
    [Platform.YouTube]: <span className="font-bold text-[#FF0000]">{Platform.YouTube}</span>,
    [Platform.Mastodon]: <span className="font-bold text-[#6364FF]">{Platform.Mastodon}</span>,
    [Platform.Threads]: <span className="font-bold text-black dark:text-white">{Platform.Threads}</span>,
    [Platform.Bluesky]: <span className="font-bold text-[#007AFF]">{Platform.Bluesky}</span>,
    [Platform.TikTok]: <span className="font-bold text-black dark:text-white">{Platform.TikTok}</span>,
};

// Interface for form validation errors.
interface FormErrors {
    platforms?: string;
    content?: string;
    media?: string;
    schedule?: string;
    general?: string;
    facebookPages?: string;
    instagramAccounts?: string;
}

// Data structure for post validation.
interface ValidationData {
    content: string;
    selectedPlatforms: Platform[];
    mediaPreview: string | null;
    mediaType: MediaType | null;
    scheduleLater: boolean;
    scheduledAt: string;
    selectedFacebookPageIds: string[];
    hasFacebookPages: boolean;
    selectedInstagramAccountIds: string[];
    hasInstagramAccounts: boolean;
}

/**
 * Validates the post data before scheduling.
 * @param data - The post data to validate.
 * @returns An object containing any validation errors.
 */
const validatePost = ({
    content,
    selectedPlatforms,
    mediaPreview,
    mediaType,
    scheduleLater,
    scheduledAt,
    selectedFacebookPageIds,
    hasFacebookPages,
    selectedInstagramAccountIds,
    hasInstagramAccounts,
}: ValidationData): FormErrors => {
    const newErrors: FormErrors = {};

    if (selectedPlatforms.length === 0) {
      newErrors.platforms = 'Please select at least one platform.';
    }
    
    if (!content.trim()) {
      newErrors.content = 'Content cannot be empty.';
    } else {
        // Check character limits for each selected platform.
        for (const platform of selectedPlatforms) {
            const limit = platformLimits[platform];
            if (limit && content.length > limit) {
                newErrors.content = `${platform} posts cannot exceed ${limit} characters.`;
                break; 
            }
        }
    }
    
    // Check platform-specific media requirements.
    const instagramSelected = selectedPlatforms.includes(Platform.Instagram);
    const instagramMissingMedia = instagramSelected && !mediaPreview;
    const instagramInvalidMedia = instagramSelected && mediaType && !['image', 'video'].includes(mediaType);
    const youtubeRequiresVideo = selectedPlatforms.includes(Platform.YouTube);
    const tiktokRequiresVideo = selectedPlatforms.includes(Platform.TikTok);
    
    if (youtubeRequiresVideo) {
      if (!mediaPreview) {
        newErrors.media = 'A video is required for YouTube.';
      } else if (mediaType !== 'video') {
        newErrors.media = 'Only videos can be uploaded to YouTube.';
      }
    } else if (tiktokRequiresVideo) {
        if (!mediaPreview) {
            newErrors.media = 'A video is required for TikTok posts.';
        } else if (mediaType !== 'video') {
            newErrors.media = 'Only videos can be posted to TikTok.';
        }
    } else if (instagramMissingMedia) {
        newErrors.media = 'An image or video is required for Instagram posts.';
    } else if (instagramInvalidMedia) {
        newErrors.media = 'Instagram supports image or video posts.';
    }

    if (selectedPlatforms.includes(Platform.Facebook)) {
      if (!hasFacebookPages) {
        newErrors.facebookPages = 'Connect a Facebook Page before publishing to Facebook.';
      } else if (selectedFacebookPageIds.length === 0) {
        newErrors.facebookPages = 'Select at least one Facebook Page for this post.';
      }
    }

    if (instagramSelected) {
      if (!hasInstagramAccounts) {
        newErrors.instagramAccounts = 'Connect an Instagram Business account before publishing to Instagram.';
      } else if (selectedInstagramAccountIds.length === 0) {
        newErrors.instagramAccounts = 'Select at least one Instagram account for this post.';
      }
      if (mediaType && !['image', 'video'].includes(mediaType)) {
        newErrors.media = 'Instagram supports image or video posts.';
      }
    }

    if (scheduleLater) {
        if (!scheduledAt) {
            newErrors.schedule = 'Please set a schedule date.';
        } else if (new Date(scheduledAt) <= new Date()) {
            newErrors.schedule = 'Scheduled time must be in the future.';
        }
    }

    return newErrors;
};


export const Composer: React.FC<ComposerProps> = ({ 
  onSchedulePost, 
  generatePostSuggestion,
  refinePostSuggestion,
  postToReuse, 
  onClearReusedPost,
  mediaToReuse,
  onClearReusedMedia,
  showSnackbar,
  facebookPages,
  facebookPagesLoading,
  onRequestFacebookConnect,
  instagramAccounts,
  instagramAccountsLoading,
  onRequestInstagramConnect,
  canPublishWithoutApproval,
}) => {
  // State for the post content.
  const [content, setContent] = useState('');
  // State for the selected platforms.
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  // State for the media preview URL (base64).
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  // State for the type of media attached.
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  // State for the description of the media attached.
  const [mediaDescription, setMediaDescription] = useState<string | null>(null);
  // State for platform-specific visibility settings.
  const [visibility, setVisibility] = useState<Partial<Record<Platform, PostVisibility>>>({});
  // State for selected Facebook Page IDs when Facebook is chosen.
  const [selectedFacebookPageIds, setSelectedFacebookPageIds] = useState<string[]>([]);
  const [selectedInstagramAccountIds, setSelectedInstagramAccountIds] = useState<string[]>([]);

  // Calculate default schedule time (10 minutes from now).
  const now = new Date();
  now.setMinutes(now.getMinutes() + 10);
  const defaultScheduleDate = now.toISOString().slice(0, 16);
  // State for the scheduled date and time.
  const [scheduledAt, setScheduledAt] = useState(defaultScheduleDate);
  // State to toggle the scheduler.
  const [scheduleLater, setScheduleLater] = useState(false);
  // Loading state for AI generation/refinement.
  const [generatingState, setGeneratingState] = useState<'idle' | 'suggestion' | 'refine_manual' | 'refine_cta'>('idle');
  const isGenerating = generatingState !== 'idle';
  // Loading state for post submission.
  const [isScheduling, setIsScheduling] = useState(false);
  // State for form validation errors.
  const [errors, setErrors] = useState<FormErrors>({});

  // State to hold the original content before an AI suggestion is applied.
  const [originalContent, setOriginalContent] = useState<string | null>(null);
  // State for the refinement instruction input.
  const [refinementInput, setRefinementInput] = useState('');
  // State to control the visibility of the suggestion generation modal.
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false);
  // State for the platform currently shown in the live preview.
  const [activePreview, setActivePreview] = useState<Platform | null>(null);

  // Draft state management
  const [draftStatus, setDraftStatus] = useState<string>('');
  const [loadedDraft, setLoadedDraft] = useState<ComposerDraft | null>(null);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Effect to check for a saved draft on component mount.
  useEffect(() => {
    const savedDraftJson = localStorage.getItem('amplifyChatDraft');
    if (savedDraftJson) {
      try {
        const draft = JSON.parse(savedDraftJson) as ComposerDraft;
        setLoadedDraft(draft);
      } catch (error) {
        console.error("Failed to parse saved draft:", error);
        localStorage.removeItem('amplifyChatDraft');
      }
    }
  }, []);

  // Effect for auto-saving the draft.
  useEffect(() => {
    // Debounce mechanism for auto-save.
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Check if there is anything to save.
    const isComposerEmpty = !content && selectedPlatforms.length === 0 && !mediaPreview;
    if (isComposerEmpty) {
      setDraftStatus('');
      return;
    }
    
    setDraftStatus('Saving...');
    autoSaveTimeoutRef.current = window.setTimeout(() => {
      const draft: ComposerDraft = {
        content,
        platforms: selectedPlatforms,
        mediaUrl: mediaPreview,
        mediaType,
        mediaDescription,
        visibility,
        scheduleLater,
        scheduledAt,
        facebookPageIds: selectedFacebookPageIds,
        instagramAccountIds: selectedInstagramAccountIds,
      };
      localStorage.setItem('amplifyChatDraft', JSON.stringify(draft));
      setDraftStatus(`Saved at ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
    }, 5000); // Save after 5 seconds of inactivity.

    // Cleanup timeout on unmount.
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [content, selectedPlatforms, mediaPreview, mediaType, mediaDescription, visibility, scheduleLater, scheduledAt, selectedFacebookPageIds]);

  // Effect to provide real-time validation feedback on character limits.
  useEffect(() => {
    // Find the first platform that exceeds its character limit.
    for (const platform of selectedPlatforms) {
        const limit = platformLimits[platform];
        if (limit && content.length > limit) {
            setErrors(prev => ({
                ...prev,
                content: `${platform} posts cannot exceed ${limit} characters.`
            }));
            return; // Exit after finding the first error.
        }
    }
    
    // If the loop completes, it means no character limits are exceeded.
    // The onChange handler for the textarea is responsible for clearing previous errors.
  }, [content, selectedPlatforms]);

  // Effect to update the active preview tab when selected platforms change.
  useEffect(() => {
    if (selectedPlatforms.length > 0) {
      if (!activePreview || !selectedPlatforms.includes(activePreview)) {
        setActivePreview(selectedPlatforms[0]);
      }
    } else {
      setActivePreview(null);
    }
  }, [selectedPlatforms, activePreview]);

  useEffect(() => {
    if (!selectedPlatforms.includes(Platform.Facebook)) {
      setSelectedFacebookPageIds([]);
      return;
    }

    const availableIds = new Set(facebookPages.map(page => page.id));
    setSelectedFacebookPageIds(prev => {
      let next = prev.filter(id => availableIds.has(id));

      if (facebookPages.length === 0) {
        next = [];
      } else if (next.length === 0) {
        next = [facebookPages[0]!.id];
      }

      if (next.length === prev.length && next.every((id, index) => prev[index] === id)) {
        return prev;
      }
      return next;
    });
  }, [facebookPages, selectedPlatforms]);

  useEffect(() => {
    if (!selectedPlatforms.includes(Platform.Instagram)) {
      setSelectedInstagramAccountIds([]);
      return;
    }
    const availableIds = new Set(instagramAccounts.map(account => account.id));
    setSelectedInstagramAccountIds(prev => {
      let next = prev.filter(id => availableIds.has(id));
      if (instagramAccounts.length === 0) {
        next = [];
      } else if (next.length === 0) {
        next = [instagramAccounts[0].id];
      }
      if (next.length === prev.length && next.every((id, index) => prev[index] === id)) {
        return prev;
      }
      return next;
    });
  }, [instagramAccounts, selectedPlatforms]);

  // Effect to populate the composer when a post is selected for reuse.
  useEffect(() => {
    if (postToReuse) {
      setContent(postToReuse.content);
      setSelectedPlatforms(postToReuse.platforms);
      setMediaPreview(postToReuse.mediaUrl);
      setMediaType(postToReuse.mediaType);
      setVisibility(postToReuse.visibility || {});
      setSelectedFacebookPageIds(postToReuse.facebookPageIds ?? []);
      setSelectedInstagramAccountIds(postToReuse.instagramAccountIds ?? []);
      document.getElementById('content')?.focus();
      onClearReusedPost(); // Clear the reused post to prevent re-populating on re-render.
    }
  }, [postToReuse, onClearReusedPost]);
  
  // Effect to populate the media uploader when media is selected for reuse.
  useEffect(() => {
    if (mediaToReuse) {
      setMediaPreview(mediaToReuse.url);
      setMediaType(mediaToReuse.type);
      setMediaDescription(mediaToReuse.description);
      document.getElementById('content')?.focus();
      onClearReusedMedia();
    }
  }, [mediaToReuse, onClearReusedMedia]);

  // Handles calling the AI service to generate a post suggestion.
  const handleGenerateSuggestion = async (prompt: string) => {
    if (!prompt.trim()) return;
    setGeneratingState('suggestion');
    setErrors(prev => ({ ...prev, general: undefined }));
    try {
        setOriginalContent(content); // Save the current content before generating.
        const generatedSuggestion = await generatePostSuggestion(prompt);
        setContent(generatedSuggestion); // Apply the suggestion directly to the composer.
        setIsSuggestionModalOpen(false); // Close modal on success.
    } catch {
        setOriginalContent(null); // Clear saved content on error.
        setErrors(prev => ({ ...prev, general: "Failed to generate suggestion." }));
    } finally {
        setGeneratingState('idle');
    }
  };

  // Handles calling the AI service to refine the current content.
  const handleRefineSuggestion = async () => {
    if (!refinementInput.trim() || !content) return;
    
    setGeneratingState('refine_manual');
    try {
        const newSuggestion = await refinePostSuggestion(content, refinementInput);
        setContent(newSuggestion);
        setRefinementInput(''); // Clear input after refinement.
    } catch {
        setErrors(prev => ({ ...prev, general: "Failed to refine suggestion." }));
    } finally {
        setGeneratingState('idle');
    }
  };

  const handleCtaRefine = async () => {
    if (!content) return;

    setGeneratingState('refine_cta');
    try {
      const instruction = "Add a clear and compelling call to action (CTA) to the post, guiding users on the next step, like 'Sign up now!' or 'Learn more.'";
      const newSuggestion = await refinePostSuggestion(content, instruction);
      setContent(newSuggestion);
      setRefinementInput(''); // Clear input as well
    } catch {
      setErrors(prev => ({ ...prev, general: "Failed to refine suggestion." }));
    } finally {
      setGeneratingState('idle');
    }
  };

  // Reverts the composer content to what it was before the AI suggestion was applied.
  const handleUndoSuggestion = () => {
    if (originalContent !== null) {
      setContent(originalContent);
    }
    setOriginalContent(null);
  };

  const clearComposer = () => {
    setContent('');
    setSelectedPlatforms([]);
    setMediaPreview(null);
    setMediaType(null);
    setMediaDescription(null);
    setScheduledAt(defaultScheduleDate);
    setScheduleLater(false);
    setVisibility({});
    setSelectedFacebookPageIds([]);
    setSelectedInstagramAccountIds([]);
    setOriginalContent(null);
    setErrors({});
  };

  // Handles form submission, validation, and scheduling the post.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formErrors = validatePost({
        content,
        selectedPlatforms,
        mediaPreview,
        mediaType,
        scheduleLater,
        scheduledAt,
        selectedFacebookPageIds,
        hasFacebookPages: facebookPages.length > 0,
        selectedInstagramAccountIds,
        hasInstagramAccounts: instagramAccounts.length > 0,
    });
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsScheduling(true);
    try {
      await onSchedulePost({
        content,
        platforms: selectedPlatforms,
        mediaUrl: mediaPreview,
        mediaType: mediaType,
        scheduledAt: scheduleLater ? new Date(scheduledAt) : new Date(),
        visibility,
        facebookPageIds: selectedFacebookPageIds,
        instagramAccountIds: selectedInstagramAccountIds,
      }, mediaDescription);
      clearComposer();
      localStorage.removeItem('amplifyChatDraft');
      setDraftStatus('');
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    } finally {
      setIsScheduling(false);
    }
  };
  
  // Handles changes in platform selection and updates visibility settings accordingly.
  const handlePlatformChange = (platforms: Platform[]) => {
    setSelectedPlatforms(platforms);
    setErrors(prev => ({...prev, platforms: undefined, media: undefined, content: undefined, facebookPages: undefined, instagramAccounts: undefined }));

    const nextVisibility = { ...visibility };
    
    // Remove visibility settings for platforms that are no longer selected.
    for (const p in nextVisibility) {
        if (!platforms.includes(p as Platform)) {
            delete nextVisibility[p as Platform];
        }
    }
    
    // Add default visibility for newly selected platforms that support it.
    for (const p of platforms) {
        if (PLATFORMS_WITH_VISIBILITY.includes(p) && !nextVisibility[p]) {
            nextVisibility[p] = 'public';
        }
    }

    setVisibility(nextVisibility);

    if (!platforms.includes(Platform.Facebook)) {
      setSelectedFacebookPageIds([]);
    } else if (facebookPages.length > 0 && selectedFacebookPageIds.length === 0) {
      setSelectedFacebookPageIds([facebookPages[0].id]);
    }

    if (!platforms.includes(Platform.Instagram)) {
      setSelectedInstagramAccountIds([]);
    } else if (instagramAccounts.length > 0 && selectedInstagramAccountIds.length === 0) {
      setSelectedInstagramAccountIds([instagramAccounts[0].id]);
    }
  }

  const handleToggleFacebookPage = (pageId: string) => {
    setSelectedFacebookPageIds(prev => prev.includes(pageId)
      ? prev.filter(id => id !== pageId)
      : [...prev, pageId]);
    setErrors(prev => ({ ...prev, facebookPages: undefined }));
  };

  const handleToggleInstagramAccount = (accountId: string) => {
    setSelectedInstagramAccountIds(prev => prev.includes(accountId)
      ? prev.filter(id => id !== accountId)
      : [...prev, accountId]);
    setErrors(prev => ({ ...prev, instagramAccounts: undefined }));
  };

  const handleSaveDraft = () => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    const draft: ComposerDraft = {
      content,
      platforms: selectedPlatforms,
      mediaUrl: mediaPreview,
      mediaType,
      mediaDescription,
      visibility,
      scheduleLater,
      scheduledAt,
      facebookPageIds: selectedFacebookPageIds,
      instagramAccountIds: selectedInstagramAccountIds,
    };
    localStorage.setItem('amplifyChatDraft', JSON.stringify(draft));
    const newStatus = `Saved at ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    setDraftStatus(newStatus);
    showSnackbar("Draft saved successfully!");
  };

  const handleRestoreDraft = () => {
    if (loadedDraft) {
      setContent(loadedDraft.content);
      setSelectedPlatforms(loadedDraft.platforms);
      setMediaPreview(loadedDraft.mediaUrl);
      setMediaType(loadedDraft.mediaType);
      setMediaDescription(loadedDraft.mediaDescription);
      setVisibility(loadedDraft.visibility);
      setScheduleLater(loadedDraft.scheduleLater);
      setScheduledAt(loadedDraft.scheduledAt);
      setSelectedFacebookPageIds(loadedDraft.facebookPageIds ?? []);
      setSelectedInstagramAccountIds(loadedDraft.instagramAccountIds ?? []);
      setLoadedDraft(null);
      showSnackbar("Draft restored.");
    }
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem('amplifyChatDraft');
    setLoadedDraft(null);
    showSnackbar("Saved draft discarded.");
  };

  // Filter for platforms that have visibility settings.
  const platformsWithVisibilitySelected = selectedPlatforms.filter(p => PLATFORMS_WITH_VISIBILITY.includes(p));

  const isFacebookSelectionDisabled = facebookPagesLoading || facebookPages.length === 0;
  const facebookDisabledReason = facebookPagesLoading
    ? 'Loading Facebook Pages...'
    : 'Connect a Facebook Page in the Control Panel to enable Facebook posts.';
  const isInstagramSelectionDisabled = instagramAccountsLoading || instagramAccounts.length === 0;
  const instagramDisabledReason = instagramAccountsLoading
    ? 'Loading Instagram accounts...'
    : 'Connect an Instagram Business account (linked to a Facebook Page) in the Control Panel to enable Instagram image or video posts.';

  const disabledPlatformList: Platform[] = [];
  const disabledReasonMap: Partial<Record<Platform, string>> = {};

  if (isFacebookSelectionDisabled) {
    disabledPlatformList.push(Platform.Facebook);
    disabledReasonMap[Platform.Facebook] = facebookDisabledReason;
  }
  if (isInstagramSelectionDisabled) {
    disabledPlatformList.push(Platform.Instagram);
    disabledReasonMap[Platform.Instagram] = instagramDisabledReason;
  }

  return (
    <div className="space-y-6 md:space-y-8">
      {/* --- Composer Form Card --- */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200 dark:border-gray-700">
        {/* Restore Draft Banner */}
        {loadedDraft && (
          <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/50 border border-orange-200 dark:border-orange-800 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4 animate-fade-in">
            <div className="flex items-center gap-3">
              <InformationCircleIcon className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0" />
              <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">You have an unsaved draft. Would you like to restore it?</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={handleDiscardDraft} className="text-sm font-semibold text-slate-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400">Discard</button>
              <button onClick={handleRestoreDraft} className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded-lg text-sm">Restore</button>
            </div>
          </div>
        )}
        <h2 className="text-xl font-bold mb-4">Create a new post</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Platform Selector */}
          <div>
            <label htmlFor="platforms" className="block text-sm font-medium text-slate-600 dark:text-gray-400 mb-2">Platforms</label>
            <PlatformSelector 
              selectedPlatforms={selectedPlatforms} 
              onSelectionChange={handlePlatformChange}
              disabledPlatforms={disabledPlatformList}
              disabledReasons={Object.keys(disabledReasonMap).length > 0 ? disabledReasonMap : undefined}
            />
            {errors.platforms && <p className="text-red-500 dark:text-red-400 text-sm mt-2">{errors.platforms}</p>}
          </div>
          {selectedPlatforms.includes(Platform.Facebook) && (
            <div className="bg-slate-50 dark:bg-gray-700/40 border border-slate-200 dark:border-gray-600 rounded-xl p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-gray-200">Select Facebook Pages</h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400">Choose where this post should be published when Facebook is selected.</p>
                </div>
                <button
                  type="button"
                  onClick={onRequestFacebookConnect}
                  className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Manage Pages
                </button>
              </div>
              {facebookPagesLoading ? (
                <p className="text-sm text-slate-500 dark:text-gray-400">Loading Facebook Pages...</p>
              ) : facebookPages.length === 0 ? (
                <div className="text-sm text-slate-500 dark:text-gray-400">
                  No Facebook Pages connected yet. Use “Manage Pages” to connect one before posting.
                </div>
              ) : (
                <div className="space-y-2">
                  {facebookPages.map(page => {
                    const isSelected = selectedFacebookPageIds.includes(page.id);
                    return (
                      <label
                        key={page.id}
                        className={`flex items-center justify-between gap-4 p-3 rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                            : 'border-slate-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-400'
                        }`}
                      >
                        <div>
                          <p className="font-medium text-slate-700 dark:text-gray-200">{page.pageName}</p>
                          <p className={`text-xs ${page.requiresReauthorization ? 'text-yellow-600 dark:text-yellow-300' : 'text-slate-500 dark:text-gray-400'}`}>
                            {page.requiresReauthorization
                              ? 'Token expiring soon – reconnect in Control Panel'
                              : page.userTokenExpiresAt
                                ? `Token expires ${page.userTokenExpiresAt.toLocaleDateString()}`
                                : 'Long-lived access token'}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleFacebookPage(page.id)}
                          className="w-5 h-5 accent-blue-500"
                        />
                      </label>
                    );
                  })}
                </div>
              )}
              {errors.facebookPages && <p className="text-sm text-red-500 dark:text-red-400">{errors.facebookPages}</p>}
            </div>
          )}
          {selectedPlatforms.includes(Platform.Instagram) && (
            <div className="bg-slate-50 dark:bg-gray-700/40 border border-slate-200 dark:border-gray-600 rounded-xl p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-gray-200">Select Instagram Accounts</h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400">Choose which Instagram Business accounts should receive this post.</p>
                </div>
                <button
                  type="button"
                  onClick={onRequestInstagramConnect}
                  className="text-xs font-semibold text-pink-600 dark:text-pink-400 hover:underline"
                >
                  Manage Instagram Accounts
                </button>
              </div>
              {instagramAccountsLoading ? (
                <p className="text-sm text-slate-500 dark:text-gray-400">Loading Instagram accounts...</p>
              ) : instagramAccounts.length === 0 ? (
                <div className="text-sm text-slate-500 dark:text-gray-400">
                  No Instagram accounts connected yet. Use “Manage Instagram Accounts” to link one before posting images to Instagram.
                </div>
              ) : (
                <div className="space-y-2">
                  {instagramAccounts.map(account => {
                    const isSelected = selectedInstagramAccountIds.includes(account.id);
                    return (
                      <label
                        key={account.id}
                        className={`flex items-center justify-between gap-4 p-3 rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-pink-500 bg-pink-50 dark:border-pink-400 dark:bg-pink-900/30'
                            : 'border-slate-200 dark:border-gray-600 hover:border-pink-300 dark:hover:border-pink-400'
                        }`}
                      >
                        <div className="space-y-1">
                          <p className="font-medium text-slate-700 dark:text-gray-200">@{account.username}</p>
                          <p className={`text-xs ${account.requiresReauthorization ? 'text-yellow-600 dark:text-yellow-300' : 'text-slate-500 dark:text-gray-400'}`}>
                            {account.requiresReauthorization
                              ? 'Token expiring soon – reconnect in Control Panel'
                              : account.tokenExpiresAt
                                ? `Token expires ${account.tokenExpiresAt.toLocaleDateString()}`
                                : 'Long-lived access token'}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleInstagramAccount(account.id)}
                          className="w-5 h-5 accent-pink-500"
                        />
                      </label>
                    );
                  })}
                </div>
              )}
              {errors.instagramAccounts && <p className="text-sm text-red-500 dark:text-red-400">{errors.instagramAccounts}</p>}
            </div>
          )}
          {/* Content Textarea */}
          <div className="relative">
            <label htmlFor="content" className="block text-sm font-medium text-slate-600 dark:text-gray-400 mb-2">Content</label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setErrors(prev => ({...prev, content: undefined}));
              }}
              placeholder="What's on your mind?"
              className={`w-full h-36 bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-3 border focus:ring-2 focus:outline-none transition ${
                errors.content
                  ? 'border-red-500 dark:border-red-400 focus:ring-red-500/50'
                  : 'border-slate-300 dark:border-gray-600 focus:ring-blue-500 dark:focus:ring-blue-400'
              }`}
              rows={5}
            />
            {/* Character Counters */}
            <div className="flex justify-end flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
              {selectedPlatforms.map(platform => {
                const limit = platformLimits[platform];
                if (limit === undefined) return null;
                const isExceeded = content.length > limit;
                return (
                  <span key={platform} className={isExceeded ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-slate-500 dark:text-gray-400'}>
                    {platform}: {content.length}/{limit}
                  </span>
                );
              })}
            </div>
            {errors.content && <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.content}</p>}
            {/* AI Suggestion Button */}
            <Tooltip text="Generate with AI" position="left">
              <button 
                type="button" 
                onClick={() => setIsSuggestionModalOpen(true)}
                disabled={isGenerating}
                className="absolute bottom-3 right-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:bg-gray-500 text-white p-2 rounded-full transition-all duration-200 transform hover:scale-110 disabled:cursor-not-allowed"
              >
                {generatingState === 'suggestion' ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : <MagicWandIcon className="w-5 h-5" />}
              </button>
            </Tooltip>
          </div>

          {/* AI Suggestion Loading and Refinement UI */}
          {generatingState === 'suggestion' && (
            <div className="mt-4 p-4 text-center bg-slate-100 dark:bg-gray-700/50 rounded-lg">
              <p className="text-slate-600 dark:text-gray-300 animate-pulse">Generating suggestion...</p>
            </div>
          )}

          {originalContent !== null && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-gray-900/50 border border-blue-200 dark:border-gray-700 rounded-lg space-y-4 animate-fade-in-up">
                <div>
                  <h3 className="text-sm font-bold text-slate-700 dark:text-gray-200 mb-2">✨ AI suggestion applied. Edit it directly or refine it below.</h3>
                </div>
                
                <div className="space-y-2">
                    <label htmlFor="refinement-input" className="text-xs font-medium text-slate-500 dark:text-gray-400">Want changes? Tell the AI how to refine it.</label>
                    <div className="flex items-center gap-2">
                        <input 
                          id="refinement-input"
                          type="text" 
                          value={refinementInput}
                          onChange={(e) => setRefinementInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !isGenerating && refinementInput.trim() && handleRefineSuggestion()}
                          placeholder="e.g., make it shorter, add emojis"
                          className="w-full bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-2 text-sm border border-slate-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:outline-none transition"
                          disabled={isGenerating}
                        />
                        <button 
                          type="button"
                          onClick={handleRefineSuggestion} 
                          disabled={isGenerating || !refinementInput.trim()}
                          className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-bold py-2 px-3 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {generatingState === 'refine_manual' ? (
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : 'Refine'}
                        </button>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-slate-500 dark:text-gray-400">Quick Actions:</span>
                      <button
                        type="button"
                        onClick={handleCtaRefine}
                        disabled={isGenerating}
                        className="text-xs bg-slate-200 dark:bg-gray-700 hover:bg-slate-300 dark:hover:bg-gray-600 text-slate-700 dark:text-slate-200 font-semibold py-1 px-2.5 rounded-full transition disabled:opacity-50"
                      >
                        {generatingState === 'refine_cta' ? (
                          <svg className="animate-spin h-4 w-4 text-slate-700 dark:text-slate-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          'Add CTA'
                        )}
                      </button>
                    </div>
                </div>
                
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-blue-200 dark:border-gray-700">
                  <button 
                    type="button"
                    onClick={handleUndoSuggestion} 
                    className="text-sm font-semibold text-slate-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    Undo Suggestion
                  </button>
                </div>
            </div>
          )}

          {/* Media Uploader */}
          <div>
            <label htmlFor="media" className="block text-sm font-medium text-slate-600 dark:text-gray-400 mb-2">
              Media
              {(selectedPlatforms.includes(Platform.Instagram) || selectedPlatforms.includes(Platform.YouTube) || selectedPlatforms.includes(Platform.TikTok)) && (
                <span className="text-red-500 dark:text-red-400 text-xs ml-2">(required)</span>
              )}
            </label>
            <MediaUploader 
              mediaPreview={mediaPreview}
              mediaType={mediaType} 
              onMediaChange={(base64, type, description) => {
                setMediaPreview(base64);
                setMediaType(type);
                setMediaDescription(description || null);
                setErrors(prev => ({...prev, media: undefined}));
              }}
              hasError={!!errors.media}
            />
            {errors.media && <p className="text-red-500 dark:text-red-400 text-sm mt-2">{errors.media}</p>}
          </div>
          
          {/* Platform-specific Visibility Settings */}
          {platformsWithVisibilitySelected.length > 0 && (
            <div className="space-y-4 pt-2 border-t border-slate-200 dark:border-gray-700">
               <h3 className="text-sm font-medium text-slate-600 dark:text-gray-400 -mb-2">Platform Settings</h3>
               {platformsWithVisibilitySelected.map(platform => {
                  const platformVisibility = visibility[platform] || 'public';
                  return (
                    <div key={platform}>
                      <label htmlFor={`visibility-${platform}`} className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-gray-400 mb-2">
                        {platformDisplay[platform]}
                        <span>Visibility</span>
                      </label>
                      <div className="relative">
                        <select
                          id={`visibility-${platform}`}
                          value={platformVisibility}
                          onChange={(e) => setVisibility(prev => ({ ...prev, [platform]: e.target.value as PostVisibility }))}
                          className="w-full appearance-none bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-3 border border-slate-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:outline-none transition pl-10"
                        >
                          <option value="public">Public</option>
                          <option value="friends">Friends</option>
                          <option value="private">Only Me (Private)</option>
                        </select>
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 dark:text-gray-400">
                          {platformVisibility === 'public' && <GlobeIcon className="w-5 h-5" />}
                          {platformVisibility === 'friends' && <UsersIcon className="w-5 h-5" />}
                          {platformVisibility === 'private' && <LockClosedIcon className="w-5 h-5" />}
                        </div>
                      </div>
                    </div>
                  )
               })}
            </div>
          )}

          {/* Scheduling Options */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-slate-600 dark:text-gray-400">Schedule for later</span>
              <label htmlFor="schedule-toggle" className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  id="schedule-toggle" 
                  className="sr-only peer" 
                  checked={scheduleLater}
                  onChange={() => {
                    setScheduleLater(!scheduleLater)
                    setErrors(prev => ({...prev, schedule: undefined}));
                  }}
                />
                <div className="w-11 h-6 bg-slate-300 dark:bg-gray-600 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 dark:peer-checked:bg-orange-500"></div>
              </label>
            </div>
            {scheduleLater && (
              <div>
                <button
                  type="button"
                  onClick={() => dateInputRef.current?.showPicker()}
                  className={`flex items-center text-left gap-3 w-full bg-slate-100 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded-lg p-3 border cursor-pointer transition ${
                    errors.schedule
                      ? 'border-red-500 dark:border-red-400'
                      : 'border-slate-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'
                  }`}
                >
                  <CalendarIcon className="w-5 h-5 text-slate-500 dark:text-gray-400 flex-shrink-0" />
                  <span className="flex-grow text-sm font-medium">
                    {scheduledAt ? new Date(scheduledAt).toLocaleString([], {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }) : 'Select a date and time'}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  type="datetime-local"
                  id="schedule"
                  value={scheduledAt}
                  onChange={(e) => {
                    setScheduledAt(e.target.value);
                    setErrors(prev => ({...prev, schedule: undefined}));
                  }}
                  className="opacity-0 w-0 h-0 absolute"
                />
                {errors.schedule && <p className="text-red-500 dark:text-red-400 text-sm mt-2">{errors.schedule}</p>}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4 border-t border-slate-200 dark:border-gray-700 space-y-4">
            <div className="flex flex-col-reverse sm:flex-row items-center gap-3">
              <div className="flex items-center justify-center sm:justify-start gap-3 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="w-full sm:w-auto bg-slate-200 hover:bg-slate-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-slate-700 dark:text-gray-200 font-bold py-3 px-4 rounded-lg transition-colors text-sm"
                  >
                    Save Draft
                  </button>
                  <span className="text-xs text-slate-500 dark:text-gray-400 flex-shrink-0">{draftStatus}</span>
              </div>
              <button 
                type="submit"
                disabled={isScheduling}
                className="w-full sm:flex-grow bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg transition-transform transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isScheduling
                    ? 'Scheduling...'
                    : scheduleLater
                      ? 'Schedule Post'
                      : (canPublishWithoutApproval ? 'Publish Now' : 'Request Approval')}
              </button>
            </div>
            {errors.general && <p className="text-red-500 dark:text-red-400 text-sm mt-2 text-center">{errors.general}</p>}
          </div>
        </form>
        <GenerateSuggestionModal 
          isOpen={isSuggestionModalOpen}
          onClose={() => setIsSuggestionModalOpen(false)}
          onGenerate={handleGenerateSuggestion}
          isGenerating={generatingState === 'suggestion'}
        />
        {/* CSS for animations */}
        <style>{`
          @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in-up {
            animation: fade-in-up 0.3s ease-out forwards;
          }
           @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out forwards;
          }
        `}</style>
      </div>
      {/* --- Live Preview Card --- */}
      <div>
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200 dark:border-gray-700 min-h-[500px]">
          <h2 className="text-xl font-bold mb-4 border-b border-slate-200 dark:border-gray-700 pb-3">Live Preview</h2>
          {activePreview ? (
            <div>
              <div className="flex -mt-2 mb-4">
                {selectedPlatforms.map(p => (
                  <button
                    key={p}
                    onClick={() => setActivePreview(p)}
                    className={`px-3 sm:px-4 py-2 text-sm font-semibold border-b-2 transition-colors focus:outline-none ${
                      activePreview === p
                        ? 'border-orange-500 text-orange-500 dark:text-orange-400'
                        : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="bg-slate-50 dark:bg-gray-900/50 p-4 rounded-xl">
                 <PlatformPreview
                    platform={activePreview}
                    content={content}
                    mediaUrl={mediaPreview}
                    mediaType={mediaType}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-96 bg-slate-50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-slate-500 dark:text-gray-400 text-center px-4">Select a platform to see a live preview of your post.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
